import {
  action, observable,
} from 'mobx';
import AddressRepository, { IAddressRepositoryConfig } from '../AddressRepository';
import Address, { AddressId, IAddressAttributes } from '../../domain/entities/Address';
import { ADDRESS_TYPE, FIELD_TYPE } from '../../constants/Constants';
import { IFromEstimatedDeliveryMapping } from '../../api/IDataMappings';
import { ADDRESS, RESTRICTION_ERRORS } from '../../constants/Errors';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE, SHIPPING_PAYMENT_NOTIFICATIONS } from '../../constants/Notifications';
import { IApiErrorElement } from '../../api';
import { INotificationResponse } from '../../api/IApiResponses';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';

export type IShippingAddressRepositoryConfig = IAddressRepositoryConfig;

class ShippingRepository extends AddressRepository {
  @observable userAddress: IAddressAttributes;

  @observable upsAddress: IAddressAttributes;

  @observable public isEditing = false;

  constructor(props: IShippingAddressRepositoryConfig) {
    super(props);
    this.addressType = ADDRESS_TYPE.SHIPPING;
  }

  @action
  setUpsAddress = (
    userAddress: IAddressAttributes, upsAddress: IAddressAttributes,
  ): void => {
    this.userAddress = userAddress;
    this.upsAddress = upsAddress;
  };

  @action
  public onSelectAddress = async (id: AddressId): Promise<void> => {
    try {
      await this.api.selectShippingAddress(id);
      if (this.config.jsonSchemasEnabled) {
        const address = this.getAddressById(id);
        await this.loadAddressSchema(address.attributes[FIELD_TYPE.COUNTRY_ID]);
      }
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.SHIPPING);
    }
  };

  @action
  public updateAddress = async (address: Address): Promise<Address> => {
    try {
      this.setIsUpdating(true);
      const addressData = Address.toJSON(address);

      addressData[FIELD_TYPE.ZIPCODE] = addressData[FIELD_TYPE.ZIPCODE].toString().toUpperCase();

      const { address: updatedAddressData, errors } = await this.api.updateShippingAddress(
        addressData,
        addressData[FIELD_TYPE.ID] || address.id,
        this.config.useE164PhoneStandard,
      );

      const updatedAddress = Address.fromJSON(updatedAddressData);

      this.updateAddressValues(updatedAddress);

      if (updatedAddress.isDefault) {
        this._addresses.forEach(addr => addr !== updatedAddress && addr.setDefault(false));
      }

      if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS)) {
        this.notificationsCollection.clearNotificationByKey(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS);
      }

      if (errors) {
        this.setHazmatShippingError(errors);
        if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY)) {
          throw new Error(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY);
        }

        if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.SHIPPING_UNAVAILABLE_FOR_ADDRESS)) {
          throw new Error(RESTRICTION_ERRORS.SHIPPING_UNAVAILABLE_FOR_ADDRESS);
        }
      }

      return updatedAddress;
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.SHIPPING);
    } finally {
      this.setIsUpdating(false);
    }
  };

  @action
  public saveNewAddress = async (address: Address): Promise<Address> => {
    try {
      this.setIsSavingNew(true);

      const addressData = Address.toJSON(address);

      addressData[FIELD_TYPE.ZIPCODE] = addressData[FIELD_TYPE.ZIPCODE].toString().toUpperCase();

      const response: {
        address?: IAddressAttributes;
        errors?: IApiErrorElement[];
        notifications?: INotificationResponse[];
      } = await this.api.createShippingAddress(addressData, this.config.useE164PhoneStandard);

      if (response.errors) {
        this.setHazmatShippingError(response.errors);

        if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY)) {
          throw new Error(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY);
        }

        if (this.notificationsCollection.hasNotification(ADDRESS.INVALID_AUPOST_QAS_ADDRESS)) {
          throw new Error(ADDRESS.INVALID_AUPOST_QAS_ADDRESS);
        }
      }

      if (response.notifications?.some(notification => notification.key === SHIPPING_PAYMENT_NOTIFICATIONS.SHIPPING_SET_TO_DEFAULT)) {
        this.notificationsCollection.addNotification({
          group: NOTIFICATION_GROUP.SHIPPING_PAYMENT,
          key: SHIPPING_PAYMENT_NOTIFICATIONS.SHIPPING_SET_TO_DEFAULT,
          timeout: 30000,
          type: NOTIFICATION_TYPE.WARNING,
        });
      }

      const newAddress = Address.fromJSON(response.address);

      if (newAddress.isDefault) {
        this._addresses.forEach(addr => addr.setDefault(false));
      }

      this.addAddress(newAddress);

      return newAddress;
    } catch (error) {
      this.handleAddressError(error, NOTIFICATION_GROUP.SHIPPING);
    } finally {
      this.setIsSavingNew(false);
    }
  };

  @action
  public deleteShippingAddress = async (id: AddressId): Promise<void> => {
    try {
      await this.api.deleteShippingAddress(id);
    } catch (error) {
      const normalizedErrors = normalizeApiErrors(error, NOTIFICATION_GROUP.SHIPPING);
      this.notificationsCollection.addNotifications(normalizedErrors);
    }
  };

  @action
  public setEditingAddress(isEditing: boolean): void {
    this.isEditing = isEditing;
  }

  public getEstimatedDeliveryDate = async (location?: AddressId | IAddressAttributes): Promise<IFromEstimatedDeliveryMapping> => {
    let address: IAddressAttributes;
    if (location) {
      // Get EDD for a valid Zip Code
      const schema = this.formStore.getAddressValidationSchema(
        this.config && this.config.shippingAddressInputSchema,
      );
      const zipPatternRegex = new RegExp(
        schema.properties.postal_code.pattern,
      );
      if (typeof location === 'string') {
        address = Address.toJSON(this.getAddressById(location));
      } else if (location[FIELD_TYPE.ZIPCODE]) {
        if (zipPatternRegex.test(location[FIELD_TYPE.ZIPCODE])) {
          address = location;
        }
      }
    }
    
    if (address) {
      address[FIELD_TYPE.COUNTRY_ID] = this.config.countryId;
      return this.api.getEstimatedDeliveryDate(address);
    }
  };
}

export default ShippingRepository;
